import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { moneda, cantidad } from '../utils/format';
import { useAuth } from '../contexts/AuthContext';

const emptyForm = {
  nombre: '', sku: '', categoria_id: '', unidad_id: '',
  precio_costo: '', precio_venta: '', stock_actual: '', stock_minimo: '',
  controla_stock: true,
};

// ── Formulario ─────────────────────────────────────────────────────────────
function ProductoForm({ form, onChange, categorias, unidades, isEdit }) {
  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange({ ...form, [field]: val });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <label>Nombre *</label>
          <input className="input" value={form.nombre} onChange={set('nombre')} placeholder="Ej: Pollo entero" />
        </div>
        <div>
          <label>SKU / Código</label>
          <input className="input" value={form.sku} onChange={set('sku')} placeholder="Opcional" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Categoría</label>
          <select className="input" value={form.categoria_id} onChange={set('categoria_id')}>
            <option value="">Sin categoría</option>
            {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label>Unidad de medida</label>
          <select className="input" value={form.unidad_id} onChange={set('unidad_id')}>
            <option value="">Sin unidad</option>
            {unidades.map(u => <option key={u.id} value={u.id}>{u.nombre} ({u.simbolo})</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Precio costo ($)</label>
          <input className="input" type="number" min="0" step="0.01"
            value={form.precio_costo} onChange={set('precio_costo')} placeholder="0.00" />
        </div>
        <div>
          <label>Precio venta ($)</label>
          <input className="input" type="number" min="0" step="0.01"
            value={form.precio_venta} onChange={set('precio_venta')} placeholder="0.00" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {!isEdit && (
          <div>
            <label>Stock inicial</label>
            <input className="input" type="number" min="0" step="0.001"
              value={form.stock_actual} onChange={set('stock_actual')} placeholder="0" />
          </div>
        )}
        {isEdit && (
          <div>
            <label>Stock actual</label>
            <input className="input bg-slate-50 text-slate-400 cursor-not-allowed" readOnly
              value={form.stock_actual} title="El stock se modifica mediante compras, ventas y ajustes" />
          </div>
        )}
        <div>
          <label>Stock mínimo</label>
          <input className="input" type="number" min="0" step="0.001"
            value={form.stock_minimo} onChange={set('stock_minimo')} placeholder="0" />
        </div>
      </div>

      <div className="flex items-center gap-2.5 pt-1">
        <input
          id="controla_stock"
          type="checkbox"
          checked={!!form.controla_stock}
          onChange={set('controla_stock')}
          className="w-4 h-4 accent-primary-500 cursor-pointer"
        />
        <label htmlFor="controla_stock" className="!mb-0 !uppercase-none text-[13px] text-slate-600 font-medium cursor-pointer normal-case tracking-normal">
          Controlar stock para este producto
        </label>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function Productos() {
  const { can } = useAuth();

  const [productos,   setProductos]   = useState([]);
  const [categorias,  setCategorias]  = useState([]);
  const [unidades,    setUnidades]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [catFiltro,   setCatFiltro]   = useState('');

  const [modalOpen,  setModalOpen]  = useState(false);
  const [editItem,   setEditItem]   = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);

  const [confirmId,  setConfirmId]  = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  const puedeGestionar = can('productos.gestionar');

  // ── Carga ────────────────────────────────────────────────────────────────
  const fetchProductos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)    params.search       = search;
      if (catFiltro) params.categoria_id = catFiltro;
      const res = await api.get('/productos/index.php', { params });
      setProductos(res.data);
    } catch {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, [search, catFiltro]);

  useEffect(() => { fetchProductos(); }, [fetchProductos]);

  useEffect(() => {
    api.get('/categorias/index.php').then(r => setCategorias(r.data)).catch(() => {});
    api.get('/unidades/index.php').then(r => setUnidades(r.data)).catch(() => {});
  }, []);

  // ── Modal ────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({
      nombre:         p.nombre,
      sku:            p.sku ?? '',
      categoria_id:   p.categoria_id ?? '',
      unidad_id:      p.unidad_id    ?? '',
      precio_costo:   p.precio_costo,
      precio_venta:   p.precio_venta,
      stock_actual:   p.stock_actual,
      stock_minimo:   p.stock_minimo,
      controla_stock: p.controla_stock,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/productos/index.php?id=${editItem.id}`, form);
        toast.success('Producto actualizado');
      } else {
        await api.post('/productos/index.php', form);
        toast.success('Producto creado');
      }
      setModalOpen(false);
      fetchProductos();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Eliminar ─────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/productos/index.php?id=${confirmId}`);
      toast.success('Producto eliminado');
      setConfirmId(null);
      fetchProductos();
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Productos</h1>
        {puedeGestionar && (
          <button className="btn-primary" onClick={openCreate}>
            <Plus size={18} /> Nuevo producto
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Buscar por nombre o SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="input sm:w-48" value={catFiltro} onChange={(e) => setCatFiltro(e.target.value)}>
          <option value="">Todas las categorías</option>
          {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : productos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300">
            <Package size={48} strokeWidth={1.5} />
            <p className="text-sm mt-3 text-slate-400">No hay productos{search ? ' con ese criterio' : ''}</p>
            {puedeGestionar && !search && (
              <button className="btn-primary mt-4" onClick={openCreate}><Plus size={16} /> Crear el primero</button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>SKU</th>
                  <th>Categoría</th>
                  <th>Unidad</th>
                  <th>P. Costo</th>
                  <th>P. Venta</th>
                  <th>Stock</th>
                  <th>Estado</th>
                  {puedeGestionar && <th></th>}
                </tr>
              </thead>
              <tbody>
                {productos.map((p) => {
                  const sinStock  = p.controla_stock && p.stock_actual === 0;
                  const bajoStock = p.controla_stock && p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo;
                  return (
                    <tr key={p.id}>
                      <td className="font-medium text-slate-900">{p.nombre}</td>
                      <td className="text-slate-400 text-xs font-mono">{p.sku || <span className="text-slate-200">—</span>}</td>
                      <td className="text-slate-500">{p.categoria || <span className="text-slate-300">—</span>}</td>
                      <td className="text-slate-500">{p.unidad || <span className="text-slate-300">—</span>}</td>
                      <td className="tabular-nums">{moneda(p.precio_costo)}</td>
                      <td className="tabular-nums font-medium">{moneda(p.precio_venta)}</td>
                      <td className={`tabular-nums font-semibold ${sinStock ? 'text-red-600' : bajoStock ? 'text-orange-500' : 'text-slate-900'}`}>
                        {p.controla_stock ? cantidad(p.stock_actual, p.unidad) : <span className="text-slate-300 text-xs">no controla</span>}
                      </td>
                      <td>
                        {!p.controla_stock
                          ? <span className="badge-neutral">Servicio</span>
                          : sinStock  ? <span className="badge-low">Sin stock</span>
                          : bajoStock ? <span className="badge-low">Bajo stock</span>
                          : <span className="badge-ok">OK</span>}
                      </td>
                      {puedeGestionar && (
                        <td>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => setConfirmId(p.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && productos.length > 0 && (
        <p className="text-xs text-slate-400">{productos.length} producto/s</p>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? 'Editar producto' : 'Nuevo producto'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editItem ? 'Guardar cambios' : 'Crear producto'}
            </button>
          </>
        }
      >
        <ProductoForm
          form={form}
          onChange={setForm}
          categorias={categorias}
          unidades={unidades}
          isEdit={!!editItem}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Eliminar producto"
        message="¿Seguro que querés eliminar este producto? No afecta el historial de movimientos."
      />
    </div>
  );
}
